// Two kinds of handle:
//  - "vertex": an existing point — dragging it moves that point (editNetwork
//    .ts's `moveVertex`); double-clicking deletes it (`removeVertexAt`).
//  - "midpoint": the middle of a segment between two consecutive points —
//    dragging it inserts a brand new point there (`insertVertexAt`) and
//    continues the drag as if it were that new vertex.

import type { Point, SwmmLink, SwmmProject } from "./types.ts";
import type { VertexTarget } from "./editNetwork.ts";
import type { ElementRef } from "./properties.ts";
import { getCaseInsensitive } from "./idLookup.ts";

interface VertexHandle {
  target: VertexTarget;
  /** Index within this element's own point list (VERTICES for a link, POLYGONS for a subcatchment) — NOT a raw-row index. */
  index: number;
  point: Point;
  kind: "vertex" | "midpoint";
}

function midpoint(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

export function linkVertexHandles(
  link: SwmmLink,
  from: Point,
  to: Point,
  vertices: Point[]
): VertexHandle[] {
  const target: VertexTarget = { section: "VERTICES", name: link.name };
  const path = [from, ...vertices, to];
  const handles: VertexHandle[] = [];
  for (let i = 1; i < path.length - 1; i++) {
    handles.push({ target, index: i - 1, point: path[i], kind: "vertex" });
  }
  for (let i = 0; i < path.length - 1; i++) {
    handles.push({ target, index: i, point: midpoint(path[i], path[i + 1]), kind: "midpoint" });
  }
  return handles;
}

/** A closed ring — the last point's midpoint wraps back to the first. */
export function polygonVertexHandles(subcatchmentName: string, polygon: Point[]): VertexHandle[] {
  const target: VertexTarget = { section: "POLYGONS", name: subcatchmentName };
  const handles: VertexHandle[] = polygon.map((point, index) => ({
    target,
    index,
    point,
    kind: "vertex",
  }));
  polygon.forEach((point, i) => {
    const next = polygon[(i + 1) % polygon.length];
    handles.push({ target, index: i + 1, point: midpoint(point, next), kind: "midpoint" });
  });
  return handles;
}

/** A link's [VERTICES] are optional and can drop to zero, but a subcatchment's [POLYGONS] outline needs at least 3 points to still be a polygon, so deleting is refused once it's already at 3. */
export function canRemoveVertex(target: VertexTarget, project: SwmmProject): boolean {
  if (target.section !== "POLYGONS") return true;
  return (project.polygons.get(target.name)?.length ?? 0) > 3;
}

export function vertexPointCount(project: SwmmProject, ref: ElementRef): number {
  if (ref.category === "link") return project.vertices.get(ref.name)?.length ?? 0;
  if (ref.category === "subcatchment") return project.polygons.get(ref.name)?.length ?? 0;
  return 0;
}

export function vertexTargetFor(ref: ElementRef): VertexTarget | null {
  if (ref.category === "link") return { section: "VERTICES", name: ref.name };
  if (ref.category === "subcatchment") return { section: "POLYGONS", name: ref.name };
  return null;
}

export function vertexPointAt(project: SwmmProject, ref: ElementRef, index: number): Point | null {
  const points =
    ref.category === "link"
      ? project.vertices.get(ref.name)
      : ref.category === "subcatchment"
        ? project.polygons.get(ref.name)
        : undefined;
  return points?.[index] ?? null;
}

export function keyboardInsertPoint(
  project: SwmmProject,
  ref: ElementRef,
  afterIndex: number | null
): Point | null {
  if (ref.category === "link") {
    const link = project.links.find((l) => l.name === ref.name);
    if (!link) return null;
    const vertices = project.vertices.get(ref.name) ?? [];
    const from = getCaseInsensitive(project.coordinates, link.fromNode) ?? null;
    const to = getCaseInsensitive(project.coordinates, link.toNode) ?? null;
    const prev = afterIndex === null ? from : (vertices[afterIndex] ?? null);
    const next = afterIndex === null ? (vertices[0] ?? to) : (vertices[afterIndex + 1] ?? to);
    return prev && next ? midpoint(prev, next) : null;
  }
  if (ref.category === "subcatchment") {
    const polygon = project.polygons.get(ref.name) ?? [];
    if (polygon.length === 0) return null;
    const idx = afterIndex ?? polygon.length - 1;
    const prev = polygon[idx] ?? null;
    const next = polygon[(idx + 1) % polygon.length] ?? null;
    return prev && next ? midpoint(prev, next) : null;
  }
  return null;
}

export function keyboardInsertIndex(afterIndex: number | null): number {
  return afterIndex === null ? 0 : afterIndex + 1;
}
